// The permission grid's arithmetic (docs/ai-connections-brief.md, Part 2 outcome a): the areas across, one level in each
// cell, worked out from the permission keys a person holds and the mapping of area and level to keys
// (permission_area_key rows, from data/seed-permission-areas.csv). Levels are cumulative: Suggest holds every See key
// too, Change every Suggest key, Manage every Change key (2026-10-23, docs/ai-employee-needs-brief.md: Change edits what
// exists; Manage also creates new rows and retires them). Pure: the database's ug.area_level says the same thing.

export type AreaLevel = 'none' | 'see' | 'suggest' | 'change' | 'manage';
export const AREA_LEVELS: AreaLevel[] = ['none', 'see', 'suggest', 'change', 'manage'];
/** The levels a key maps to: every level but none. */
export const KEYED_LEVELS = ['see', 'suggest', 'change', 'manage'] as const;
export type KeyedLevel = (typeof KEYED_LEVELS)[number];
export const LEVEL_LABELS: Record<AreaLevel | 'owner', string> = { none: 'None', see: 'See', suggest: 'Suggest', change: 'Change', manage: 'Manage', owner: 'owner' };

/** The levels in order, so one can be compared to another: none 0 … manage 4, the owner above them all. */
export function levelRank(level: AreaLevel | 'owner' | string | null | undefined): number {
  switch (level) {
    case 'see': return 1;
    case 'suggest': return 2;
    case 'change': return 3;
    case 'manage': return 4;
    case 'owner': return 9;
    default: return 0;
  }
}
/** Does a level hold at least another? The owner holds everything. */
export const atLeast = (held: AreaLevel | 'owner' | string | null | undefined, wanted: AreaLevel): boolean => levelRank(held) >= levelRank(wanted);

export interface AreaKeyRow {
  area_key: string;
  level: KeyedLevel;
  permission_key: string;
}

/** The keys an area's level maps to, on its own. */
export function keysOf(map: AreaKeyRow[], area: string, level: KeyedLevel): string[] {
  return map.filter((r) => r.area_key === area && r.level === level).map((r) => r.permission_key);
}

/** Every key a level holds in an area: its own and those of the levels under it. */
export function keysUpTo(map: AreaKeyRow[], area: string, level: AreaLevel): string[] {
  const out: string[] = [];
  for (const l of KEYED_LEVELS) {
    out.push(...keysOf(map, area, l));
    if (l === level) break;
  }
  return level === 'none' ? [] : out;
}

export interface AreaStanding {
  level: AreaLevel;
  /** True when the person holds some, not all, of the next level's keys: a cell set key by key before the grid. */
  partial: boolean;
}

/**
 * A person's level in an area from the keys they hold: the highest level every key of which (and of the levels
 * under it) they hold. A level with no key mapped is not held.
 */
export function areaLevel(map: AreaKeyRow[], area: string, granted: ReadonlySet<string>): AreaStanding {
  let level: AreaLevel = 'none';
  for (const l of KEYED_LEVELS) {
    const keys = keysOf(map, area, l);
    if (keys.length === 0 || !keys.every((k) => granted.has(k))) {
      return { level, partial: keys.some((k) => granted.has(k)) };
    }
    level = l;
  }
  return { level, partial: false };
}

/** What setting a cell to a level means in keys: these granted, those taken away. */
export function keysForLevel(map: AreaKeyRow[], area: string, level: AreaLevel): { grant: string[]; revoke: string[] } {
  const grant = keysUpTo(map, area, level);
  const all = keysUpTo(map, area, 'manage');
  return { grant, revoke: all.filter((k) => !grant.includes(k)) };
}

/**
 * The per-person rows that set a cell: for each key the area covers, the person's own row says granted or not
 * where that differs from the role's default, and no row where it agrees (the same rule the grid has always kept:
 * a person's row exists only for a change from their role).
 */
export function cellOverrides(map: AreaKeyRow[], area: string, level: AreaLevel, roleGranted: ReadonlySet<string>): { key: string; granted: boolean }[] {
  const { grant, revoke } = keysForLevel(map, area, level);
  const out: { key: string; granted: boolean }[] = [];
  for (const k of grant) if (!roleGranted.has(k)) out.push({ key: k, granted: true });
  for (const k of revoke) if (roleGranted.has(k)) out.push({ key: k, granted: false });
  return out;
}

/**
 * The levels a cell offers (2026-10-23): a person's cell offers See and Suggest, and Change or Manage only where it is
 * already held (the database lets only the owner write through a person's login); an AI login's cell, or a preset's,
 * offers every level up to the area's `ai_most` cap (Instructions: Suggest), and the level it holds whatever the cap.
 */
export function levelsOffered(opts: { ai: boolean; held: AreaLevel; aiMost?: AreaLevel | null }): AreaLevel[] {
  const cap = opts.ai ? levelRank(opts.aiMost ?? 'manage') : levelRank('suggest');
  return AREA_LEVELS.filter((l) => levelRank(l) <= cap || l === opts.held);
}
